import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, SettingState, TibboDataType,
} from '@appblocks/codegeneration/types';

export default class ModbusTableItem extends BaseFeature {
    constructor() {
        super();
        this.name = 'Slave Register';
        this.id = 'modbus_table_item';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: 'MODItem',
            },
            {
                id: 'DATATYPE',
                name: 'Data Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'COIL',
                options: [
                    {
                        name: 'COIL/Output',
                        value: 'COIL',
                    },
                    {
                        name: 'Contact/Discrete Input',
                        value: 'INPUT',
                    },
                    {
                        name: 'Holding Register',
                        value: 'HREG',
                    },
                    {
                        name: 'Input Register',
                        value: 'IREG',
                    },
                ],
            },
            {
                id: 'VARTYPE',
                name: 'Value Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'INTEGER',
                options: [
                    {
                        name: 'Integer',
                        value: 'INTEGER',
                    },
                    {
                        name: '16-bit Signed Integer',
                        value: 'SIGNED_INTEGER',
                    },
                    // {
                    //     name: 'Floating Point Number',
                    //     value: 'FLOAT',
                    // },
                ],
            },
            {
                id: 'ADDRESS',
                name: 'Address',
                settingType: SettingType.STRING,
                required: true,
                default: '0',
            },
        ]);
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'NAME');
    }

    getState(settingId: string): SettingState {
        const registerType = this.getSettingValue('DATATYPE');
        if (settingId === 'VARTYPE') {
            if (registerType === 'HREG' || registerType === 'IREG') {
                return SettingState.ENABLED;
            }
        }
        switch (registerType) {
            case 'COIL':
            case 'INPUT':
                switch (settingId) {
                    case 'VARTYPE':
                        return SettingState.HIDDEN;
                    default:
                        return SettingState.ENABLED;
                }
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];
        const NAME = this.getSettingValue('NAME');
        const ADDRESS = this.getSettingValue('ADDRESS');
        const DATATYPE = this.getSettingValue('DATATYPE');
        const varName = `${NAME}_addr`;
        const VARTYPE = this.getSettingValue('VARTYPE');

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${varName} = ${ADDRESS}`,
        });

        switch (DATATYPE) {
            case 'COIL':
                blocks.push({
                    location: 'modbus_callback_ReadCoilStatus',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:
        modbus_callback_ReadCoilStatus=(${NAME}<>0)
        '****${NAME}_COIL_READ****'`,
                });
                blocks.push({
                    location: 'modbus_callback_WriteSingleCoil',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:
        modbus_callback_WriteSingleCoil=${NAME}
        var_${NAME}_update_completed(value)
        '****${NAME}_COIL_WRITE****'`,
                });
                break;
            case 'INPUT':// Contact/Discrete Input
                blocks.push({
                    location: 'modbus_callback_ReadInputStatus',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:
        modbus_callback_ReadInputStatus=${NAME}
        '****${NAME}_INPUT_READ****'`,
                });
                break;
            case 'HREG':// holding register
                blocks.push({
                    location: 'modbus_callback_ReadHoldingRegister',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:
        modbus_callback_ReadHoldingRegister=${NAME}
        '****${NAME}_HREG_READ****'`,
                });
                blocks.push({
                    location: 'modbus_callback_set_holding_register',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:   
${
    VARTYPE === 'SIGNED_INTEGER'
        ? `
                if data>32767 then
                    short_data = data - 65536
                    var_${NAME}_update_completed(short_data)    
                else
                    var_${NAME}_update_completed(data)    
                end if 
                    `
        : `
                var_${NAME}_update_completed(data)
            `
}
        modbus_callback_set_holding_register=true
        '****${NAME}_HREG_WRITE****'`,
                });
                break;
            case 'IREG':// Input Register
                blocks.push({
                    location: 'modbus_callback_ReadInputRegister',
                    blockType: BlockType.CODE,
                    template: `
    case ${varName}:
        modbus_callback_ReadInputRegister=${NAME}
        '****${NAME}_IREG_READ****'`,
                });
                break;
            default:

                break;
        }

        return blocks;
    }
}
